import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ClickToCopyId } from "@/components/common/ClickToCopyId";
import { QueryLoadError } from "@/components/common/QueryLoadError";
import { QRCodeSVG } from "qrcode.react";
import { CheckCircle2, ChevronDown } from "lucide-react";
import {
  Alert,
  Button,
  Input,
  Label,
  Modal,
  ModalFooter,
  Spinner,
  EmptyState,
} from "@/components/ui";
import { deviceService } from "@/services/deviceService";
import {
  devicePresetKeys,
  devicePresetService,
} from "@/services/devicePresetService";
import type { DeviceCreateResponse } from "@/types";
import { buildKioskCredentialsQrValue } from "@/lib/kioskCredentialsQr";
import { getApiErrorMessage } from "@/lib/errorMessages";
import { queryLoadFailure } from "@/lib/queryLoadFailure";

interface CreateDeviceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function CreateDeviceModal({
  isOpen,
  onClose,
  onSuccess,
}: CreateDeviceModalProps) {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("devices");
  const { t: tErrors } = useTranslation("errors");
  const [hardwareId, setHardwareId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [googleAccount, setGoogleAccount] = useState("");
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [androidVersion, setAndroidVersion] = useState("");
  const [displayResolution, setDisplayResolution] = useState("");
  const [displayInches, setDisplayInches] = useState("");
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [manualMode, setManualMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdDevice, setCreatedDevice] =
    useState<DeviceCreateResponse | null>(null);

  const presetsQuery = useQuery({
    queryKey: devicePresetKeys.lists(),
    queryFn: () => devicePresetService.getAll(),
    enabled: isOpen && !createdDevice,
  });
  const { data: presets = [] } = presetsQuery;
  const presetsLoadError = queryLoadFailure(
    presetsQuery,
    tErrors("loadDevicePresets"),
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!hardwareId.trim()) {
      setError(t("createModal.hardwareIdRequired"));
      return;
    }

    if (!googleAccount.trim()) {
      setError(t("createModal.googleAccountRequired"));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let response: DeviceCreateResponse;
      if (!manualMode) {
        if (!selectedPresetId) {
          setError(t("createModal.presetRequired"));
          setIsLoading(false);
          return;
        }
        response = await deviceService.create({
          display_name: displayName.trim() || undefined,
          serial_number: hardwareId.trim(),
          google_account: googleAccount.trim(),
          device_preset_id: selectedPresetId,
        });
      } else {
        const parsedDisplayInches = displayInches.trim()
          ? Number.parseFloat(displayInches)
          : undefined;
        if (
          parsedDisplayInches !== undefined &&
          Number.isNaN(parsedDisplayInches)
        ) {
          setError(t("createModal.inchesInvalid"));
          setIsLoading(false);
          return;
        }
        response = await deviceService.create({
          display_name: displayName.trim() || undefined,
          serial_number: hardwareId.trim(),
          google_account: googleAccount.trim(),
          brand: brand.trim() || undefined,
          model: model.trim() || undefined,
          android_version: androidVersion.trim() || undefined,
          display_resolution: displayResolution.trim() || undefined,
          display_inches: parsedDisplayInches,
        });
      }
      setCreatedDevice(response);
      resetDraft();
    } catch (err) {
      setError(getApiErrorMessage(err, "createDevice"));
    } finally {
      setIsLoading(false);
    }
  };

  const resetDraft = () => {
    setHardwareId("");
    setDisplayName("");
    setGoogleAccount("");
    setBrand("");
    setModel("");
    setAndroidVersion("");
    setDisplayResolution("");
    setDisplayInches("");
    setSelectedPresetId(null);
    setManualMode(false);
    setError(null);
  };

  const handleClose = () => {
    if (createdDevice) {
      onSuccess();
      setCreatedDevice(null);
    }
    resetDraft();
    onClose();
  };

  const qrValue = useMemo(
    () =>
      createdDevice
        ? buildKioskCredentialsQrValue(
            createdDevice.kiosk_code,
            createdDevice.password,
          )
        : "",
    [createdDevice],
  );

  return (
    <Modal
      closeLabel={tCommon("close")}
      isOpen={isOpen}
      onClose={handleClose}
      title={
        createdDevice
          ? t("createModal.titleCreated")
          : t("createModal.titleCreate")
      }
      size="lg"
      footer={
        <ModalFooter>
          {createdDevice ? (
            <Button type="button" onClick={handleClose}>
              {tCommon("close")}
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={isLoading}
              >
                {tCommon("cancel")}
              </Button>
              <Button
                type="submit"
                form="create-device-form"
                isLoading={isLoading}
              >
                {t("createModal.submit")}
              </Button>
            </>
          )}
        </ModalFooter>
      }
    >
          {!createdDevice ? (
            <form
              id="create-device-form"
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <Input
                label={t("createModal.displayNameLabel")}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={t("createModal.displayNamePlaceholder")}
                disabled={isLoading}
              />

              <Input
                label={t("createModal.hardwareIdLabel")}
                value={hardwareId}
                onChange={(e) => setHardwareId(e.target.value)}
                placeholder={t("createModal.hardwareIdPlaceholder")}
                disabled={isLoading}
                required
              />

              <Input
                type="email"
                label={t("createModal.googleAccountLabel")}
                value={googleAccount}
                onChange={(e) => setGoogleAccount(e.target.value)}
                placeholder={t("createModal.googleAccountPlaceholder")}
                disabled={isLoading}
                required
              />

              <div className="space-y-2">
                <Label as="p">{t("createModal.presetSelectorTitle")}</Label>
                {presetsQuery.isLoading ? (
                  <div className="flex justify-center py-4">
                    <Spinner size="sm" variant="muted"  loadingLabel={tCommon("loading")} />
                  </div>
                ) : presetsLoadError ? (
                  <QueryLoadError failure={presetsLoadError} />
                ) : presets.length === 0 ? (
                  <EmptyState
                    size="sm"
                    showIcon={false}
                    title={t("createModal.presetsEmpty")}
                    className="items-start px-0 py-0 text-left"
                  />
                ) : (
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    {presets.map((preset) => (
                      <button
                        type="button"
                        key={preset.id}
                        onClick={() => {
                          setManualMode(false);
                          setSelectedPresetId(preset.id);
                        }}
                        aria-pressed={selectedPresetId === preset.id && !manualMode}
                        className={`relative rounded-lg border px-3.5 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                          selectedPresetId === preset.id && !manualMode
                            ? "border-primary-500 bg-primary-50 ring-1 ring-primary-500"
                            : "border-surface-200 bg-surface-0 hover:border-surface-300 hover:bg-surface-50"
                        }`}
                      >
                        {selectedPresetId === preset.id && !manualMode ? (
                          <CheckCircle2
                            className="absolute right-3 top-3 h-4 w-4 text-primary-600"
                            aria-hidden
                          />
                        ) : null}
                        <p className="pr-6 text-sm font-medium text-surface-900">
                          {[preset.brand, preset.model].filter(Boolean).join(" ") ||
                            preset.brand}
                        </p>
                        <p className="mt-0.5 text-xs tabular-nums text-surface-500">
                          {t("createModal.presetCardSpecs", {
                            version: preset.android_version,
                            resolution: preset.display_resolution,
                            inches: preset.display_inches,
                          })}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <details
                className="group rounded-lg border border-surface-200 bg-surface-0"
                open={manualMode}
                onToggle={(e) => {
                  const open = (e.target as HTMLDetailsElement).open;
                  setManualMode(open);
                  if (open) setSelectedPresetId(null);
                }}
              >
                <summary className="flex cursor-pointer select-none items-center justify-between gap-2 rounded-lg px-3.5 py-2.5 text-sm font-medium text-surface-700 hover:bg-surface-50 hover:text-surface-900 dark:hover:bg-surface-100/50 [&::-webkit-details-marker]:hidden">
                  {t("createModal.manualToggle")}
                  <ChevronDown
                    className="h-4 w-4 shrink-0 text-surface-400 transition-transform group-open:rotate-180"
                    aria-hidden
                  />
                </summary>
                <div className="space-y-4 border-t border-surface-200 px-3.5 py-3.5">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Input
                      label={t("shared.brand")}
                      value={brand}
                      onChange={(e) => setBrand(e.target.value)}
                      placeholder={t("createModal.brandPlaceholder")}
                      disabled={isLoading}
                    />
                    <Input
                      label={t("createModal.modelLabel")}
                      value={model}
                      onChange={(e) => setModel(e.target.value)}
                      placeholder={t("createModal.modelPlaceholder")}
                      disabled={isLoading}
                    />
                    <Input
                      label={t("createModal.androidVersionLabel")}
                      value={androidVersion}
                      onChange={(e) => setAndroidVersion(e.target.value)}
                      placeholder={t("createModal.androidVersionPlaceholder")}
                      disabled={isLoading}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Input
                      label={t("createModal.resolutionLabel")}
                      value={displayResolution}
                      onChange={(e) => setDisplayResolution(e.target.value)}
                      placeholder={t("createModal.resolutionPlaceholder")}
                      disabled={isLoading}
                    />
                    <Input
                      type="number"
                      min="0"
                      step="0.1"
                      label={t("createModal.inchesLabel")}
                      value={displayInches}
                      onChange={(e) => setDisplayInches(e.target.value)}
                      placeholder={t("createModal.inchesPlaceholder")}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </details>

              {error ? <Alert variant="error">{error}</Alert> : null}
            </form>
          ) : (
            <div className="space-y-6">
              <Alert variant="success" title={t("createModal.successTitle")}>
                {t("createModal.successDescription")}
              </Alert>

              <div className="grid gap-5 md:grid-cols-2">
                <div className="space-y-4">
                  <div>
                    <Label as="p" spacing="relaxed">
                      {t("createModal.deviceNameLabel")}
                    </Label>
                    <div className="rounded-lg border border-surface-200 bg-surface-50 px-3 py-2 text-sm font-medium text-surface-900">
                      {createdDevice.display_name}
                    </div>
                  </div>

                  <div>
                    <Label as="p" spacing="relaxed">
                      {t("createModal.kioskCodeLabel")}
                    </Label>
                    <ClickToCopyId value={createdDevice.kiosk_code} />
                  </div>

                  <div>
                    <Label as="p" spacing="relaxed">
                      {t("createModal.passwordLabel")}
                    </Label>
                    <ClickToCopyId value={createdDevice.password} />
                  </div>
                </div>

                <div className="flex flex-col items-center justify-center rounded-lg border border-surface-200 bg-surface-50 p-4">
                  <div className="overflow-hidden rounded-lg ring-1 ring-surface-200">
                    <QRCodeSVG value={qrValue} size={180} includeMargin />
                  </div>
                  <p className="mt-3 text-center text-xs text-surface-600">
                    {t("createModal.qrHelp")}
                  </p>
                </div>
              </div>

              <Alert variant="warning" title={t("createModal.importantLabel")}>
                {t("createModal.importantDescription")}
              </Alert>
            </div>
          )}
    </Modal>
  );
}
